import React from 'react';
import { Box, Typography, Card, CardContent, Avatar, Button, TextField, Chip, Divider, FormControlLabel, Checkbox, InputAdornment, IconButton, Alert } from '@mui/material';
import { Person as PersonIcon, Edit as EditIcon, Save as SaveIcon, Cancel as CancelIcon, Visibility, VisibilityOff } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { getApiUrl, API_ENDPOINTS } from '../config/api';

const Profile: React.FC = () => {
  const { user, token, refreshUser, setTokenFromServer } = useAuth();
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = React.useState(false);
  const [formData, setFormData] = React.useState({
    email: user?.email || '',
    workPhone: user?.workPhone || '',
    phoneNumber: user?.phoneNumber || '',
    contactsVisible: user?.contactsVisible || false
  });
  const [pw, setPw] = React.useState({ current: '', next: '', confirm: '' });
  const [show, setShow] = React.useState({ current: false, next: false, confirm: false });
  const [pwNotice, setPwNotice] = React.useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [profileError, setProfileError] = React.useState<{ message: string; fieldErrors?: Record<string, string> } | null>(null);
  const currentEmpty = pw.current.trim() === '';
  const newShort = pw.next.length > 0 && pw.next.length < 8;
  const mismatch = pw.confirm.length > 0 && pw.next !== pw.confirm;

  // Update formData when user changes
  React.useEffect(() => {
    setFormData({
      email: user?.email || '',
      workPhone: user?.workPhone || '',
      phoneNumber: user?.phoneNumber || '',
      contactsVisible: user?.contactsVisible || false
    });
  }, [user]);

  const handleEdit = () => {
    setFormData({
      email: user?.email || '',
      workPhone: user?.workPhone || '',
      phoneNumber: user?.phoneNumber || '',
      contactsVisible: user?.contactsVisible || false
    });
    setProfileError(null);
    setIsEditing(true);
  };

  const handleSave = async () => {
    setProfileError(null);
    
    try {
      // Prepare data - handle empty email strings properly
      const updateData: any = {};
      
      // Only include email if it's not empty
      if (formData.email && formData.email.trim() !== '') {
        updateData.email = formData.email.trim();
      }
      
      // Include phone numbers (allow empty strings to clear, but only if they're defined)
      if (formData.workPhone !== undefined) {
        updateData.workPhone = formData.workPhone || '';
      }
      if (formData.phoneNumber !== undefined) {
        updateData.phoneNumber = formData.phoneNumber || '';
      }
      
      // Ensure contactsVisible is a boolean (always include it since it's a checkbox)
      updateData.contactsVisible = Boolean(formData.contactsVisible);

      const response = await fetch(getApiUrl(API_ENDPOINTS.USER_PROFILE), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Profile updated successfully:', result);
        
        // Refresh user data to show updated information
        await refreshUser();
        setIsEditing(false);
        setProfileError(null);
        
        // Show success message
        alert('Profile updated successfully!');
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Failed to parse error response' }));
        console.error('Failed to update profile:', errorData);
        
        // Parse validation errors
        if (errorData.errors && Array.isArray(errorData.errors)) {
          const fieldErrors: Record<string, string> = {};
          errorData.errors.forEach((err: any) => {
            if (err.path) {
              fieldErrors[err.path] = err.msg || err.message || 'Invalid value';
            }
          });
          
          setProfileError({
            message: errorData.error || 'Validation failed. Please check the fields below.',
            fieldErrors
          });
        } else {
          // Show error message with details if available
          const errorMessage = errorData.error || 'Failed to update profile. Please try again.';
          const detailsMessage = errorData.details ? ` ${errorData.details}` : '';
          setProfileError({
            message: errorMessage + detailsMessage
          });
        }
      }
    } catch (error: any) {
      console.error('Error saving profile:', error);
      setProfileError({
        message: error.message || 'Error saving profile. Please try again.'
      });
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setProfileError(null);
    // Reset form data to original values
    setFormData({
      email: user?.email || '',
      workPhone: user?.workPhone || '',
      phoneNumber: user?.phoneNumber || '',
      contactsVisible: user?.contactsVisible || false
    });
  };

  const handleChangePassword = async () => {
    setPwNotice(null);
    if (!pw.current || !pw.next || !pw.confirm) {
      setPwNotice({ type: 'error', text: t('profile.passwordError') });
      return;
    }
    if (pw.next !== pw.confirm || pw.next.length < 8) {
      setPwNotice({ type: 'error', text: t('profile.passwordError') });
      return;
    }
    try {
      const res = await fetch(getApiUrl(API_ENDPOINTS.CHANGE_PASSWORD), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ currentPassword: pw.current, newPassword: pw.next, confirmNewPassword: pw.confirm })
      });
      const data = await res.json();
      if (!res.ok) {
        setPwNotice({ type: 'error', text: data.error || t('profile.passwordError') });
        return;
      }
      if (data.token) setTokenFromServer(data.token);
      setPw({ current: '', next: '', confirm: '' });
      setPwNotice({ type: 'success', text: t('profile.passwordChanged') });
    } catch (e) {
      setPwNotice({ type: 'error', text: t('profile.passwordError') });
    }
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
        {t('profile.profile')}
      </Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 3 }}>
        {/* Main Profile Card */}
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    fontSize: '2rem',
                    bgcolor: 'primary.main'
                  }}
                >
                  {user?.firstName?.[0]}{user?.lastName?.[0]}
                </Avatar>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                    {user?.firstName} {user?.lastName}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {user?.email}
                  </Typography>
                  {/* Role display removed - use permission templates instead */}
                </Box>
              </Box>
              
              {!isEditing ? (
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={handleEdit}
                >
                  {t('profile.editProfile')}
                </Button>
              ) : (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSave}
                    size="small"
                  >
                    {t('profile.save')}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<CancelIcon />}
                    onClick={handleCancel}
                    size="small"
                  >
                    {t('profile.cancel')}
                  </Button>
                </Box>
              )}
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Error display */}
            {profileError && (
              <Alert severity="error" onClose={() => setProfileError(null)} sx={{ mb: 2 }}>
                {profileError.message}
              </Alert>
            )}

            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
              gap: 3 
            }}>
              {/* Read-only fields */}
              <TextField
                fullWidth
                label={t('profile.firstName')}
                value={user?.firstName || ''}
                disabled={true}
                variant="filled"
              />
              <TextField
                fullWidth
                label={t('profile.lastName')}
                value={user?.lastName || ''}
                disabled={true}
                variant="filled"
              />
              <TextField
                fullWidth
                label={t('profile.department')}
                value={user?.department || ''}
                disabled={true}
                variant="filled"
              />
              <TextField
                fullWidth
                label={t('profile.position')}
                value={user?.position || ''}
                disabled={true}
                variant="filled"
              />
              <TextField
                fullWidth
                label={t('profile.employeeId')}
                value={user?.employeeId || ''}
                disabled={true}
                variant="filled"
              />
              <TextField
                fullWidth
                label={t('profile.etatas')}
                value={user?.etatas ? `${(user.etatas * 100).toFixed(0)}%` : '100%'}
                disabled={true}
                variant="filled"
                helperText={t('profile.etatasHelp', 'Employment fraction (0-100%)')}
              />
              <TextField
                fullWidth
                label={t('profile.role', 'Role (Deprecated)')}
                value={t('profile.roleDeprecated', 'Use Permission Templates')}
                disabled={true}
                variant="filled"
              />

              {/* Editable fields */}
              <TextField
                fullWidth
                label={t('profile.email')}
                value={isEditing ? formData.email : (user?.email || '')}
                onChange={(e) => {
                  setFormData(prev => ({ ...prev, email: e.target.value }));
                  // Clear error when user starts typing
                  if (profileError?.fieldErrors?.email) {
                    setProfileError(prev => {
                      if (!prev) return null;
                      const newFieldErrors = { ...prev.fieldErrors };
                      delete newFieldErrors.email;
                      return { ...prev, fieldErrors: newFieldErrors };
                    });
                  }
                }}
                disabled={!isEditing}
                variant={isEditing ? 'outlined' : 'filled'}
                error={!!profileError?.fieldErrors?.email}
                helperText={profileError?.fieldErrors?.email}
                sx={{ gridColumn: { xs: '1', sm: '1 / -1' } }}
              />
              <TextField
                fullWidth
                label={t('profile.workPhone')}
                value={isEditing ? formData.workPhone : (user?.workPhone || '')}
                onChange={(e) => {
                  setFormData(prev => ({ ...prev, workPhone: e.target.value }));
                  if (profileError?.fieldErrors?.workPhone) {
                    setProfileError(prev => {
                      if (!prev) return null;
                      const newFieldErrors = { ...prev.fieldErrors };
                      delete newFieldErrors.workPhone;
                      return { ...prev, fieldErrors: newFieldErrors };
                    });
                  }
                }}
                disabled={!isEditing}
                variant={isEditing ? 'outlined' : 'filled'}
                error={!!profileError?.fieldErrors?.workPhone}
                helperText={profileError?.fieldErrors?.workPhone}
              />
              <TextField
                fullWidth
                label={t('profile.phoneNumber')}
                value={isEditing ? formData.phoneNumber : (user?.phoneNumber || '')}
                onChange={(e) => {
                  setFormData(prev => ({ ...prev, phoneNumber: e.target.value }));
                  if (profileError?.fieldErrors?.phoneNumber) {
                    setProfileError(prev => {
                      if (!prev) return null;
                      const newFieldErrors = { ...prev.fieldErrors };
                      delete newFieldErrors.phoneNumber;
                      return { ...prev, fieldErrors: newFieldErrors };
                    });
                  }
                }}
                disabled={!isEditing}
                variant={isEditing ? 'outlined' : 'filled'}
                error={!!profileError?.fieldErrors?.phoneNumber}
                helperText={profileError?.fieldErrors?.phoneNumber}
              />
              
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                gridColumn: { xs: '1', sm: '1 / -1' }
              }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isEditing ? formData.contactsVisible : (user?.contactsVisible || false)}
                      disabled={!isEditing}
                      onChange={(e) => {
                        if (isEditing) {
                          setFormData(prev => ({ ...prev, contactsVisible: e.target.checked }));
                        }
                      }}
                    />
                  }
                  label={t('profile.contactsVisible')}
                />
                
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Side Stats Card */}
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 3 }}>
              {t('profile.accountStatistics')}
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  {t('profile.accountStatus')}
                </Typography>
                <Chip
                  label={t('profile.active')}
                  color="success"
                  size="small"
                />
              </Box>
              
              <Box>
                <Typography variant="body2" color="text.secondary">
                  {t('profile.emailVerification')}
                </Typography>
                <Chip
                  label={t('profile.verified')}
                  color="success"
                  size="small"
                />
              </Box>
              
              <Box>
                <Typography variant="body2" color="text.secondary">
                  {t('profile.approvalStatus')}
                </Typography>
                <Chip
                  label={t(`profile.approvalStatusValues.${user?.approvalStatus || 'pending'}`, user?.approvalStatus || 'pending')}
                  color={user?.approvalStatus === 'approved' ? 'success' : user?.approvalStatus === 'rejected' ? 'error' : 'warning'}
                  size="small"
                />
              </Box>
              
              <Box>
                <Typography variant="body2" color="text.secondary">
                  {t('profile.lastLogin')}
                </Typography>
                <Typography variant="body2">
                  {user?.lastLogin ? new Date(user.lastLogin).toLocaleDateString() : t('profile.never')}
                </Typography>
              </Box>
              
              <Box>
                <Typography variant="body2" color="text.secondary">
                  {t('profile.memberSince')}
                </Typography>
                <Typography variant="body2">
                  {t('profile.january2024')}
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 3 }} />
            <Typography variant="h6" sx={{ mb: 2 }}>
              {t('profile.securitySettings')}
            </Typography>
            {pwNotice && (
              <Alert severity={pwNotice.type} onClose={() => setPwNotice(null)} sx={{ mb: 2 }}>
                {pwNotice.text}
              </Alert>
            )}
            <TextField
              fullWidth
              label={t('profile.currentPassword')}
              type={show.current ? 'text' : 'password'}
              value={pw.current}
              onChange={(e) => setPw(prev => ({ ...prev, current: e.target.value }))}
              sx={{ mb: 2 }}
              required
              error={currentEmpty && pw.current !== ''}
              helperText={currentEmpty && pw.current !== '' ? t('profile.passwordError') : ''}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShow(prev => ({ ...prev, current: !prev.current }))} edge="end">
                      {show.current ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
              autoComplete="current-password"
            />
            <TextField
              fullWidth
              label={t('profile.newPassword')}
              type={show.next ? 'text' : 'password'}
              value={pw.next}
              onChange={(e) => setPw(prev => ({ ...prev, next: e.target.value }))}
              sx={{ mb: 2 }}
              required
              error={newShort}
              helperText={newShort ? t('profile.passwordError') : ''}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShow(prev => ({ ...prev, next: !prev.next }))} edge="end">
                      {show.next ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
              autoComplete="new-password"
            />
            <TextField
              fullWidth
              label={t('profile.confirmPassword')}
              type={show.confirm ? 'text' : 'password'}
              value={pw.confirm}
              onChange={(e) => setPw(prev => ({ ...prev, confirm: e.target.value }))}
              sx={{ mb: 2 }}
              required
              error={mismatch}
              helperText={mismatch ? t('profile.passwordError') : ''}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShow(prev => ({ ...prev, confirm: !prev.confirm }))} edge="end">
                      {show.confirm ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
              autoComplete="new-password"
            />
            <Button variant="contained" onClick={handleChangePassword} disabled={currentEmpty || pw.next.length < 8 || mismatch}>
              {t('profile.changePassword')}
            </Button>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default Profile; 